<template lang="pug">
  v-text-field(
    :value="localValue",
    v-bind="$attrs",
    outlined,
    single-line,
    v-on="inputListeners",
    color="secondary",
    ref="input",
    @paste="onPaste",
    tabindex="0"
    :title="localValue"
  )
    slot(
      v-for="(_, name) in $slots",
      :name="name",
      :slot="name",
    )
</template>

<script>
import masker from 'vue-the-mask/src/masker';
import tokens from 'vue-the-mask/src/tokens';

export default {
  name: 'BaseFormInput',
  inheritAttrs: false,
  props: {
    value: [String, Number, Boolean],
    returnMaskedValue: {
      type: Boolean,
      default: false,
    },
    mask: String,
    maskTokens: {
      type: Object,
      default: () => ({}),
    },
    prepend: {
      type: Object,
      default: () => {},
    },
    integer: Boolean,
    disallowSpecialChars: Boolean,
    disallowNumbers: Boolean,
    allowChars: Array,
    disallowChars: [Array],
  },
  data() {
    return {
      localValue: '',
    };
  },
  computed: {
    unmaskedLocalValue() {
      return this.getUnmasked(this.localValue);
    },
    returnValue() {
      return (!this.mask || this.returnMaskedValue) ? this.localValue : this.unmaskedLocalValue;
    },
    inputListeners() {
      const listeners = {
        ...this.$listeners,
        input: this.onInput,
        change: this.onChange,
      };
      if (this.mask) {
        listeners.input = this.onInputWithMask;
      } else if (this.integer) {
        listeners.input = this.onInputInteger;
      }
      if (this.integer) {
        listeners.keydown = this.onIntegerKeyDown;
        listeners.beforeinput = this.onIntegerBeforeInput;
      } else if (this.isNeedToFilterChars) {
        listeners.keydown = this.onFilterSpecialCharsKeyDown;
        listeners.beforeinput = this.onFilterSpecialCharsKeyDown;
      }
      return listeners;
    },
    isNeedToFilterChars() {
      return this.disallowSpecialChars || Boolean(this.disallowChars) || this.disallowNumbers;
    },
    disallowCharsRegex() {
      return new RegExp(`[${this.specialCharacters}]`, 'g');
    },
    specialCharacters() {
      let charsString = '';
      if (this.disallowSpecialChars) charsString += '@&#+$~%^:*?<>{}[]!)(_/\\|=`;"';
      if (this.disallowNumbers) charsString += '0123456789';
      if (this.disallowChars) charsString += this.disallowChars;

      if (this.allowChars) {
        charsString.forEach((char) => {
          charsString = charsString.replace(char, '');
        });
      }

      return this.getEscapedRegExp(charsString);
    },
  },
  watch: {
    value: {
      handler(value) {
        if (this.mask) {
          if (this.unmaskedLocalValue !== value) {
            this.localValue = this.getMasked(value);
          }
        } else {
          this.localValue = value;
        }
        if (value !== this.returnValue && this.returnValue) this.emitValue();
      },
      immediate: true,
    },
  },
  methods: {
    getEscapedRegExp(str) {
      // If you need to use any of the special characters literally (actually searching for a "*", for instance),
      // you must escape it by putting a backslash in front of it.
      // eslint-disable-next-line no-useless-escape
      return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
    },
    getMasked(value, masked = true) {
      return masker(value, this.mask, masked, { ...tokens, ...this.maskTokens });
    },
    getUnmasked(value) {
      return this.getMasked(value, false);
    },
    onInput(value) {
      if (this.isNeedToFilterChars) {
        const clearedValue = value.replace(this.disallowCharsRegex, '');
        this.setInputDisplayingValue(clearedValue);
        this.localValue = clearedValue;
      } else {
        this.localValue = value;
      }
      this.emitValue(this.returnValue);
    },
    onInputInteger(value) {
      const clearedValue = value.replace(/^0+|\D+/g, '');
      this.setInputDisplayingValue(clearedValue);
      this.localValue = clearedValue;
      this.emitValue(this.returnValue);
    },
    onInputWithMask(value) {
      const textEl = this.$refs.input;
      const el = textEl.$refs.input;
      // by default, keep cursor at same position as before the mask
      let position = el.selectionEnd;
      // save the character just inserted
      const digit = el.value[position - 1];
      // save masked value
      this.localValue = this.getMasked(value);
      // Update input lazy value if value is masked
      if (this.localValue !== value) this.$refs.input.lazyValue = this.$refs.input.value;
      // if the digit was changed, increment position until find the digit again
      while (position < this.localValue.length && this.localValue.charAt(position - 1) !== digit) {
        position += 1;
      }
      if (el === document.activeElement) {
        el.setSelectionRange(position, position);
        setTimeout(() => {
          el.setSelectionRange(position, position);
        }, 0);
      }
      this.emitValue(this.returnValue);
    },
    update() {
      this.localValue = this.value;
    },
    emitValue(value = this.returnValue) {
      this.$emit('input', value);
    },
    onIntegerKeyDown(event) {
      const { key } = event;
      if (['.', ',', '+', '-', 'e', 'E'].includes(key)) {
        event.preventDefault();
      }
    },
    onFilterSpecialCharsKeyDown(event) {
      const { key } = event;
      if (this.specialCharacters.includes(key)) {
        event.preventDefault();
      }
    },
    onIntegerBeforeInput(event) {
      if (event.data === '.') event.preventDefault();
    },
    onChange() {
      this.$emit('change', this.localValue);
    },
    // On user paste from buffer
    onPaste(e) {
      // wait until variable changed
      setTimeout(() => {
        // Trim text
        this.onInput(String(this.localValue).replace(/\t/g, ' ').replace(/ {2,}/g, ' ').trim());
        // Set value attribute to sync displaying and actual value
        e.target.setAttribute('value', this.localValue);
      });
    },
    setInputDisplayingValue(value) {
      setTimeout(() => {
        if (this.$refs.input && this.$refs.input.$refs.input) {
          this.$refs.input.lazyValue = value;
          this.$refs.input.$refs.input.setAttribute('value', value);
          this.$refs.input.$refs.input.value = value;
        }
      });
    },
  },
};
</script>
